import { AxiosError } from 'axios';
import { ErrorActorEvent } from 'xstate';
import { emptyForm } from './constants';
import {
  AIOOptionResponseEvent,
  AIOSpecResponseEvent,
  AIOsResponseEvent,
  DeleteAOIEvent,
  OnSetProviderIdsEvent,
  OnSetSpecialtiesId,
  SubmitAOIEvent,
} from './events';
import { Context, Scope } from './types';
import { formValuesToAOI } from './utils';

export const setLoadingTrue = (): Partial<Context> => {
  return {
    loading: true,
  };
};

export const setLoadingFalse = (): Partial<Context> => {
  return {
    loading: false,
  };
};

export const toggleModal = (machineScope: Scope): Partial<Context> => {
  return {
    modal: !machineScope.context.modal,
  };
};

export const setAois = (machineScope: Scope): Partial<Context> => {
  const event = machineScope.event as AIOsResponseEvent;
  return {
    loading: false,
    aois: event.output,
  };
};

export const setIdToDelete = (machineScope: Scope): Partial<Context> => {
  const event = machineScope.event as DeleteAOIEvent;
  return {
    idToDelete: event.payload.id,
  };
};
export const setSpecialtiesId = (machineScope: Scope): Partial<Context> => {
  const event = machineScope.event as OnSetSpecialtiesId;
  return {
    specialtiesId: event.payload.specialtiesId,
  };
};

export const removeAoi = (machineScope: Scope): Partial<Context> => {
  return {
    aois: machineScope.context.aois.filter((aoi) => aoi.id != machineScope.context.idToDelete),
  };
};

export const setProviderIds = (machineScope: Scope): Partial<Context> => {
  const event = machineScope.event as OnSetProviderIdsEvent;
  return {
    providerId: event.payload.providerId,
    providerCategoryId: event.payload.providerCategoryId,
  };
};

// [MCV-893-pattern] Immutable spread so XState subscribers see a new array reference.
// Clear the buffer after consuming it so a stale entry can't leak into the next submit.
export const addAOI = (machineScope: Scope): Partial<Context> => {
  const { _aoiBuffer, aois } = machineScope.context;
  if (_aoiBuffer) {
    return { aois: [...aois, _aoiBuffer], _aoiBuffer: undefined };
  }
  return { aois };
};

// [CQ3] Stage the about-to-submit AOI via an assign action so the fromPromise actor
// does not mutate context directly.
export const setAoiBuffer = (machineScope: Scope): Partial<Context> => {
  const event = machineScope.event as SubmitAOIEvent;
  return {
    _aoiBuffer: formValuesToAOI(event.values),
  };
};

export const setSpecOptions = (machineScope: Scope): Partial<Context> => {
  const event = machineScope.event as AIOSpecResponseEvent;
  return {
    formValues: { ...machineScope.context.formValues, specSelect: event.output[0] },
    AOISpecialtyOptions: event.output,
  };
};

// [MCV-1207] Inline submit error instead of a generic corner toast.
// Detect auth failures (401/403) so the copy can point the user at the right next step.
export const setSubmitError = (machineScope: Scope): Partial<Context> => {
  // Cast through `unknown` because Scope['event'] is the union of all events the
  // machine accepts and XState's ErrorActorEvent isn't part of that union; the
  // machine itself routes this action only from an invoke's onError, so the cast is safe.
  const event = machineScope.event as unknown as ErrorActorEvent<unknown>;
  const err = event.error;
  let message = "We couldn't add that area of interest. Please try again.";
  if (err && typeof err === 'object' && (err as AxiosError).isAxiosError) {
    const status = (err as AxiosError).response?.status;
    if (status === 401 || status === 403) {
      message =
        "You don't have permission to add this area of interest. Please refresh the page, and contact support if the problem continues.";
    }
  }
  // Preserve raw error in the console so devs can still diagnose without a toast.
  console.debug('[MCV-1207] AOI submit error:', err);
  return { submitError: message, loading: false };
};

export const clearSubmitError = (): Partial<Context> => {
  return { submitError: undefined };
};

// [MCV-876] Do NOT auto-select the first AOI when new options arrive.
// Auto-selection caused the form to "autofill" a random AOI the user never picked;
// reset to empty so the user must explicitly choose.
export const setAOIOptions = (machineScope: Scope): Partial<Context> => {
  const event = machineScope.event as AIOOptionResponseEvent;
  return {
    formValues: { ...machineScope.context.formValues, aoiSelect: emptyForm.aoiSelect },
    AOIOptions: event.output,
  };
};
